import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { IconLoader, IconTrash } from "../components/Icons";
import { useMsg } from "../hooks/useMsg";
import Toggle from "../components/Toggle";

type CertScope = "tenant" | "branch" | "device";

interface EetSettings {
  enabled: boolean;
  cert_scope: CertScope;
  include_tips: boolean;
  eet_mode: string;
  eic: string | null;
}

interface Branch {
  id: string;
  name: string;
  eet_premise_id: number | null;
}

interface Device {
  id: string;
  name: string;
  is_active: boolean;
}

interface EetCertificate {
  id: string;
  scope: CertScope;
  branch_id: string | null;
  branch_name: string | null;
  device_id: string | null;
  device_name: string | null;
  label: string | null;
  cert_subject_cn: string | null;
  cert_valid_from: string | null;
  cert_valid_to: string | null;
  created_at: string;
}

interface CertList {
  vault_status: "ok" | "sealed" | "unavailable" | "unconfigured";
  items: EetCertificate[];
}

const CERT_SCOPES: CertScope[] = ["tenant", "branch", "device"];

export default function OperationEet() {
  const { t } = useTranslation();
  const [eet, setEet] = useState<EetSettings | null>(null);
  const [certs, setCerts] = useState<CertList | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const setMsg = useMsg();

  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [targetId, setTargetId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [eicInput, setEicInput] = useState("");
  const [premiseInputs, setPremiseInputs] = useState<Record<string, string>>({});

  function errorText(e: any): string {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d.msg || String(d)).join("; ");
    return t("common.error");
  }

  async function load() {
    setLoading(true);
    try {
      const [eetRes, certRes, branchRes, deviceRes] = await Promise.all([
        api.get<EetSettings>("/tenant/eet-settings"),
        api.get<CertList>("/eet/certificates"),
        api.get<Branch[]>("/branches?limit=500"),
        api.get<Device[]>("/licenses/devices"),
      ]);
      setEet(eetRes.data);
      setCerts(certRes.data);
      setBranches(branchRes.data);
      setDevices(deviceRes.data.filter((d) => d.is_active));
      setEicInput(eetRes.data.eic ?? "");
      setPremiseInputs(Object.fromEntries(
        branchRes.data.map((b) => [b.id, b.eet_premise_id != null ? String(b.eet_premise_id) : ""]),
      ));
    } catch (e: any) {
      setMsg({ type: "err", text: t("opEet.loadError") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function save(patch: Partial<Pick<EetSettings, "enabled" | "cert_scope" | "include_tips">>) {
    if (!eet) return;
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await api.put<EetSettings>("/tenant/eet-settings", patch);
      setEet(data);
      setTargetId("");
      setMsg({ type: "ok", text: t("companyProfile.saved") });
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opEet.saveError") });
    } finally {
      setSaving(false);
    }
  }

  async function saveIdentity(e: React.FormEvent) {
    e.preventDefault();
    if (!eet) return;
    setSaving(true);
    setMsg(null);
    try {
      const premises = branches.map((b) => ({
        branch_id: b.id,
        premise_id: premiseInputs[b.id]?.trim() ? Number(premiseInputs[b.id]) : null,
      }));
      const { data } = await api.put<EetSettings>("/tenant/eet-settings", {
        eic: eicInput.trim(),
        premises,
      });
      setEet(data);
      setMsg({ type: "ok", text: t("companyProfile.saved") });
    } catch (err: any) {
      setMsg({ type: "err", text: errorText(err) || t("opEet.saveError") });
    } finally {
      setSaving(false);
    }
  }

  async function uploadCert(e: React.FormEvent) {
    e.preventDefault();
    if (!eet || !file || !password) return;
    if (eet.cert_scope !== "tenant" && !targetId) return;
    setUploading(true);
    setMsg(null);
    try {
      // Read the .p12 fully in memory and send it as base64 — the backend
      // test-decrypts it and writes it to Vault without touching disk.
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = "";
      for (let i = 0; i < bytes.length; i += 8192) {
        bin += String.fromCharCode(...bytes.subarray(i, i + 8192));
      }
      await api.post("/eet/certificates", {
        scope: eet.cert_scope,
        ...(eet.cert_scope === "branch" ? { branch_id: targetId } : {}),
        ...(eet.cert_scope === "device" ? { device_id: targetId } : {}),
        p12_b64: btoa(bin),
        password,
      });
      setFile(null);
      setPassword("");
      setTargetId("");
      setMsg({ type: "ok", text: t("opEet.certSaved") });
      await load();
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opEet.uploadError") });
    } finally {
      setUploading(false);
    }
  }

  async function deleteCert(id: string) {
    if (!confirm(t("opEet.deleteConfirm"))) return;
    try {
      await api.delete(`/eet/certificates/${id}`);
      await load();
    } catch (e: any) {
      setMsg({ type: "err", text: errorText(e) || t("opEet.deleteError") });
    }
  }

  if (loading || !eet || !certs) {
    return <div className="card" style={{ textAlign: "center", padding: 48 }}><IconLoader size={32} className="muted" /></div>;
  }

  const moduleActive = eet.eet_mode !== "disabled";
  if (!moduleActive) {
    return <Navigate to="/operation-settings" replace />;
  }
  const vaultReady = certs.vault_status === "ok";
  const scopeTargets = eet.cert_scope === "branch"
    ? branches.map((b) => ({ id: b.id, name: b.name }))
    : eet.cert_scope === "device"
      ? devices.map((d) => ({ id: d.id, name: d.name }))
      : [];
  const uploadedTargetIds = new Set(
    certs.items
      .filter((c) => c.scope === eet.cert_scope)
      .map((c) => (eet.cert_scope === "branch" ? c.branch_id : c.device_id)),
  );
  const missingTargets = eet.cert_scope === "tenant"
    ? []
    : scopeTargets.filter((t) => !uploadedTargetIds.has(t.id));

  return (
    <div>
      <div className="page-header">
        <h1>{t("opEet.title")}</h1>
        <p>{t("opEet.subtitle")}</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t("opEet.basicTitle")}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          {t("opEet.basicL1")}<br />
          {t("opEet.basicL2")}<br />
          {t("opEet.basicL3")}
        </p>
        <div style={{ marginBottom: 12 }}>
          <Toggle
            checked={eet.enabled}
            onChange={(checked) => void save({ enabled: checked })}
            label={t("opEet.eetActive")}
            disabled={saving}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Toggle
            checked={eet.include_tips}
            onChange={(checked) => void save({ include_tips: checked })}
            label={t("opEet.includeTips")}
            disabled={saving || !eet.enabled}
          />
          <p style={{ color: "var(--muted)", fontSize: 12, marginTop: 4 }}>
            {t("opEet.includeTipsHint")}
          </p>
        </div>
        <p style={{ color: "var(--muted)", fontSize: 13 }}>
          {t("opEet.serverMode")} <strong>{t(`opEet.mode.${eet.eet_mode}`, { defaultValue: eet.eet_mode })}</strong>
          {" · "}{t("opEet.vault")} <strong>{t(`opEet.vaultStatus.${certs.vault_status}`, { defaultValue: certs.vault_status })}</strong>
        </p>
      </div>

      <div className="card" style={{ marginBottom: 24, opacity: eet.enabled ? 1 : 0.6 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t("opEet.identityTitle")}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          {t("opEet.identityHint")}
        </p>
        <form onSubmit={saveIdentity} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 420 }}>
          <div>
            <label>{t("opEet.eic")}</label>
            <input
              type="text"
              value={eicInput}
              onChange={(e) => setEicInput(e.target.value)}
              placeholder="CZ00000000"
              disabled={saving || !eet.enabled}
              style={{ fontFamily: "monospace" }}
            />
          </div>
          {branches.map((b) => (
            <div key={b.id}>
              <label>{t("opEet.premiseFor", { name: b.name })}</label>
              <input
                type="number"
                min={1}
                max={999999999}
                value={premiseInputs[b.id] ?? ""}
                onChange={(e) => setPremiseInputs((prev) => ({ ...prev, [b.id]: e.target.value }))}
                placeholder={t("opEet.premisePh")}
                disabled={saving || !eet.enabled}
              />
            </div>
          ))}
          <div>
            <button type="submit" className="primary" disabled={saving || !eet.enabled}>
              {saving ? t("common.saving") : t("opEet.saveIdentity")}
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginBottom: 24, opacity: eet.enabled ? 1 : 0.6 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{t("opEet.certsTitle")}</h3>
        <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>
          {t("opEet.certsL1")}<br />
          {t("opEet.certsL2")}
        </p>
        <label style={{ display: "block", marginBottom: 6 }}>{t("opEet.certScope")}</label>
        <select
          value={eet.cert_scope}
          disabled={saving || !eet.enabled}
          onChange={(e) => void save({ cert_scope: e.target.value as CertScope })}
          style={{ width: 360, maxWidth: "100%" }}
        >
          {CERT_SCOPES.map((s) => (
            <option key={s} value={s}>{t(`opEet.scope.${s}`)}</option>
          ))}
        </select>

        {missingTargets.length > 0 && (
          <p style={{ color: "var(--red)", fontSize: 13, marginTop: 12 }}>
            {t("opEet.missingCerts")} {missingTargets.map((x) => x.name).join(", ")}
          </p>
        )}

        {certs.items.length > 0 && (
          <table style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>{t("opEet.colScope")}</th>
                <th>{t("opEet.colTarget")}</th>
                <th>{t("opEet.colCertCn")}</th>
                <th>{t("opEet.colValidTo")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {certs.items.map((c) => (
                <tr key={c.id}>
                  <td>{t(`opEet.scopeShort.${c.scope}`)}</td>
                  <td>{c.scope === "branch" ? c.branch_name : c.scope === "device" ? c.device_name : t("opEet.scopeShort.tenant")}</td>
                  <td>{c.cert_subject_cn ?? "—"}</td>
                  <td>{c.cert_valid_to ? new Date(c.cert_valid_to).toLocaleDateString("cs") : "—"}</td>
                  <td>
                    <button className="ghost" style={{ color: "var(--red)" }} onClick={() => void deleteCert(c.id)}>
                      <IconTrash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {eet.enabled && (
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t("opEet.uploadTitle")}</h3>
          {!vaultReady ? (
            <p style={{ color: "var(--muted)", fontSize: 14 }}>
              {t("opEet.vaultUnavailable", { status: t(`opEet.vaultStatus.${certs.vault_status}`, { defaultValue: certs.vault_status }) })}
            </p>
          ) : (
            <form onSubmit={uploadCert} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 420 }}>
              {eet.cert_scope !== "tenant" && (
                <div>
                  <label>{eet.cert_scope === "branch" ? t("opEet.scopeShort.branch") : t("opEet.scopeShort.device")}</label>
                  <select value={targetId} onChange={(e) => setTargetId(e.target.value)} required>
                    <option value="">{t("opEet.selectPlaceholder")}</option>
                    {scopeTargets.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label>{t("opEet.certFile")}</label>
                <input
                  type="file"
                  accept=".p12,.pfx,application/x-pkcs12"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  required
                />
              </div>
              <div>
                <label>{t("opEet.certPassword")}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  required
                />
              </div>
              <div>
                <button type="submit" className="primary" disabled={uploading || !file || !password}>
                  {uploading ? t("opEet.uploading") : t("opEet.uploadTitle")}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
